/**
 * AIPhishingBanner
 *
 * Warning banner for messages with medium/high phishing risk.
 * Automatically checks messages from unknown senders.
 */

import { ShieldAlert, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAIPhishing } from '../hooks/useAIPhishing';
import { cn } from '@/lib/utils';

interface AIPhishingBannerProps {
  accountId: string;
  messageId: string;
  senderInContacts: boolean;
}

export function AIPhishingBanner({
  accountId,
  messageId,
  senderInContacts,
}: AIPhishingBannerProps) {
  // Only check if sender is NOT in contacts
  const { result, loading } = useAIPhishing(accountId, messageId, !senderInContacts);

  if (senderInContacts || loading || !result || result.risk === 'low') {
    return null;
  }

  const isHigh = result.risk === 'high';

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 border-b',
        isHigh
          ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900'
          : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-900',
      )}
    >
      {isHigh ? (
        <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
      ) : (
        <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-sm font-medium',
            isHigh ? 'text-red-800 dark:text-red-200' : 'text-yellow-800 dark:text-yellow-200',
          )}
        >
          {isHigh ? 'High phishing risk detected' : 'Possible phishing attempt'}
        </div>
        {result.signals.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {result.signals.map((signal, i) => (
              <li
                key={i}
                className={cn(
                  'text-xs',
                  isHigh
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-yellow-700 dark:text-yellow-300',
                )}
              >
                • {signal}
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 mt-2">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-current">
            <ShieldAlert className="h-3 w-3" /> Report as phishing
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
            <ShieldCheck className="h-3 w-3" /> This is safe
          </Button>
        </div>
      </div>
    </div>
  );
}
